// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth, useOrganizationList } from '@/auth/clerkShim';
import { AlertTriangle, MapPin, Check, X, Loader2 } from 'lucide-react';
import _ from 'lodash';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { isSlugBlacklisted } from '@staty/shared/blacklistedSlugs';
import useOrganizationStore from '@store/organizationStore';

import { sportOptions, sportPlaceholders } from './sportOptions';
import { useLocationSearch } from './useLocationSearch';
import { useSlugAvailability } from './useSlugAvailability';
import { TENANT_ROOT_DOMAIN } from '@staty/shared/domains';

interface CreateOrganizationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  league?: any;
}

export default function CreateOrganizationModal({ open, onOpenChange, league }: CreateOrganizationModalProps) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { setActive } = useOrganizationList();
  const navigate = useNavigate();

  const { createOrganization } = useOrganizationStore((state) => ({
    createOrganization: state.createOrganization,
  }));

  const [contestType, setContestType] = useState<'league' | 'tournament' | null>(null);
  const [selectedSport, setSelectedSport] = useState<string>('basketball');
  const [useSameStripeAccount, setUseSameStripeAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const locationSearch = useLocationSearch('');
  const slug = useSlugAvailability();

  const hasStripeAccount = !!(league?.stepsTaken && league.stepsTaken.createdStripeAccount);

  const resetForm = () => {
    setContestType(null);
    setSelectedSport('basketball');
    setUseSameStripeAccount(false);
    setLoading(false);
    setSubmitError('');
    locationSearch.setLocation('');
    slug.setLeagueName('');
    slug.setCustomSlug('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetForm();
    }
    onOpenChange(next);
  };

  const canSubmit =
    !loading &&
    contestType !== null &&
    !!locationSearch.location.trim() &&
    !!selectedSport &&
    !!slug.leagueName.trim() &&
    slug.finalSlug.length >= 3 &&
    slug.slugStatus === 'available';

  const onSubmit = async () => {
    setSubmitError('');

    if (!contestType) {
      setSubmitError('Please choose League or Tournament');
      return;
    }
    if (!locationSearch.location.trim()) {
      setSubmitError('Please enter a location');
      return;
    }
    if (!slug.leagueName.trim()) {
      setSubmitError('Please enter an organization name');
      return;
    }
    if (!slug.finalSlug || slug.finalSlug.length < 3) {
      setSubmitError('URL must be at least 3 characters');
      return;
    }
    if (slug.slugStatus === 'checking') {
      setSubmitError('Please wait while we check availability');
      return;
    }
    if (slug.slugStatus === 'taken' || isSlugBlacklisted(slug.finalSlug)) {
      setSubmitError('This URL is not available. Please choose another.');
      return;
    }

    const token = await getToken();
    setLoading(true);

    createOrganization(
      slug.leagueName,
      slug.finalSlug,
      user.id,
      token,
      useSameStripeAccount && league?._id ? league._id : null,
      async (res, err) => {
        setLoading(false);
        if (err || !res) {
          setSubmitError(err || 'Something went wrong. Please try again.');
          return;
        }
        // The API returns { organization, admin } — the new org's id is the
        // reliable source. `admin.provider.organization` is a Clerk-era field
        // that is empty for Mongo-JWT admins, so reading it alone left the
        // switcher on the PREVIOUS org.
        const newOrg = _.get(res, 'organization._id') || _.get(res, 'admin.provider.organization');
        if (newOrg) {
          // MUST await: setActive POSTs /admins/auth/switch-org, and the page we
          // navigate to re-reads /admins/auth/me on mount. Navigating first
          // races that read against the switch, and when the read wins the
          // provider falls back to `admin.organizations[0]` — so the user lands
          // on an unrelated org's Get Started instead of the org they created.
          try {
            await setActive({ organization: newOrg });
          } catch (e) {
            // Non-fatal: the org exists either way, so let the user through
            // rather than stranding them in the modal.
            console.error('Failed to activate the new organization', e);
          }
        }
        resetForm();
        onOpenChange(false);
        navigate('/admin/get-started');
      },
      [selectedSport],
      contestType === 'tournament' ? 1 : 0,
      locationSearch.location.trim(),
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
          <DialogDescription>
            Spin up a new league or tournament. You can configure seasons and divisions after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Contest type */}
          <div>
            <Label className="mb-2 block text-sm font-semibold">Contest type</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setContestType('league')}
                className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-all ${
                  contestType === 'league'
                    ? 'border-foreground bg-muted/50 ring-2 ring-foreground'
                    : 'border-border hover:border-input hover:bg-muted/50'
                }`}
              >
                <span className="font-semibold text-foreground">League</span>
                <span className="text-xs text-muted-foreground">
                  Recurring seasons with standings and playoffs
                </span>
              </button>
              <button
                type="button"
                onClick={() => setContestType('tournament')}
                className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-all ${
                  contestType === 'tournament'
                    ? 'border-warning bg-warning/10 ring-2 ring-warning'
                    : 'border-border hover:border-input hover:bg-muted/50'
                }`}
              >
                <span className="font-semibold text-foreground">Tournament</span>
                <span className="text-xs text-muted-foreground">
                  Single event with brackets and elimination
                </span>
              </button>
            </div>
          </div>

          {/* Location */}
          <div>
            <Label htmlFor="org-location" className="mb-2 block text-sm font-semibold">
              Location
            </Label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MapPin className="h-4 w-4 text-muted-foreground/60" />
              </div>
              <input
                id="org-location"
                type="text"
                value={locationSearch.location}
                onChange={locationSearch.handleInputChange}
                onKeyDown={locationSearch.handleKeyDown}
                onFocus={locationSearch.handleFocus}
                onBlur={locationSearch.handleBlur}
                placeholder="e.g., Los Angeles, CA"
                autoComplete="off"
                className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {locationSearch.isSearching && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />
                </div>
              )}
              {locationSearch.showSuggestions && locationSearch.suggestions.length > 0 && (
                <div
                  ref={locationSearch.suggestionsRef}
                  className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-border bg-card shadow-lg"
                >
                  {locationSearch.suggestions.map((suggestion, index) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      onClick={() => locationSearch.handleSelectSuggestion(suggestion)}
                      onMouseEnter={() => locationSearch.setHighlightedIndex(index)}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                        index === locationSearch.highlightedIndex ? 'bg-muted' : 'hover:bg-muted/50'
                      }`}
                    >
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/60" />
                      <span className="text-foreground">{suggestion.display}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sport */}
          <div>
            <Label className="mb-2 block text-sm font-semibold">Sport</Label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {sportOptions.map((sport) => (
                <button
                  key={sport.value}
                  type="button"
                  onClick={() => setSelectedSport(sport.value)}
                  className={`relative flex flex-col items-center gap-1 rounded-xl p-2.5 transition-all ${
                    selectedSport === sport.value
                      ? 'bg-gradient-to-br ' + sport.color + ' text-white shadow-md'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <span className="text-xl">{sport.icon}</span>
                  <span
                    className={`text-[10px] font-medium ${
                      selectedSport === sport.value ? 'text-white/90' : 'text-muted-foreground'
                    }`}
                  >
                    {sport.label}
                  </span>
                  {selectedSport === sport.value && (
                    <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow">
                      <Check className="h-2.5 w-2.5 text-success" strokeWidth={3} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Organization name + URL */}
          <div>
            <Label htmlFor="org-name" className="mb-2 block text-sm font-semibold">
              Organization name
            </Label>
            <input
              id="org-name"
              type="text"
              value={slug.leagueName}
              onChange={(e) => slug.setLeagueName(e.target.value)}
              placeholder={sportPlaceholders[selectedSport] || 'Your organization name'}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            />

            {(slug.leagueUrl || slug.customSlug) && (
              <div
                className={`mt-2 rounded-md border p-3 ${
                  slug.slugStatus === 'taken'
                    ? 'border-destructive/30 bg-destructive/10'
                    : slug.slugStatus === 'available'
                    ? 'border-success/30 bg-success/10'
                    : 'border-border bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-1 items-center font-mono text-xs">
                    <input
                      type="text"
                      value={slug.customSlug || slug.leagueUrl}
                      onChange={(e) => slug.handleSlugChange(e.target.value)}
                      className={`min-w-0 flex-1 bg-transparent font-semibold outline-none ${
                        slug.slugStatus === 'taken' ? 'text-destructive' : 'text-success'
                      }`}
                      placeholder="your-org"
                    />
                    <span className="flex-shrink-0 text-muted-foreground">.{TENANT_ROOT_DOMAIN}</span>
                  </div>
                  <div className="flex-shrink-0">
                    {slug.slugStatus === 'checking' && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />
                    )}
                    {slug.slugStatus === 'available' && (
                      <Check className="h-4 w-4 text-success" />
                    )}
                    {slug.slugStatus === 'taken' && <X className="h-4 w-4 text-destructive" />}
                  </div>
                </div>

                {slug.slugStatus === 'taken' && slug.slugSuggestions.length > 0 && (
                  <div className="mt-2 border-t border-border/60 pt-2">
                    <p className="mb-1 text-[11px] font-medium text-muted-foreground">Try one of these:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {slug.slugSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => slug.selectSuggestion(suggestion)}
                          className="rounded bg-card px-2 py-1 font-mono text-[11px] font-medium text-foreground ring-1 ring-border transition-all hover:ring-input"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stripe option */}
          {hasStripeAccount && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="stripe-account"
                  checked={useSameStripeAccount}
                  onCheckedChange={(checked) => setUseSameStripeAccount(checked as boolean)}
                />
                <Label htmlFor="stripe-account" className="text-sm font-medium leading-none">
                  Use the same payout account as {league?.displayName || league?.name || 'this organization'}
                </Label>
              </div>
              {useSameStripeAccount && (
                <Alert className="border-warning/30 bg-warning/10">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertDescription className="text-warning">
                    <strong>Heads up:</strong> Payments for this new organization will be processed
                    through the existing payout account on {league?.displayName || league?.name}.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {submitError && (
            <Alert className="border-destructive/30 bg-destructive/10">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">{submitError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" onClick={onSubmit} disabled={!canSubmit}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Organization'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
